/**
 * Opening the database as a step that can say no.
 *
 * Everywhere else in the client the database is simply used — `offlineDatabase()`
 * hands back the singleton and Dexie opens it on the first read. That is right
 * for a hundred call sites and wrong for exactly one moment: the first open
 * after a deployment, which is when an upgrade runs and when the only
 * irreplaceable data on the machine is at stake.
 *
 * Two things can go wrong there, and they need different answers.
 *
 * An upgrade callback that loses rows is caught inside the version-change
 * transaction by `preserving()`, and IndexedDB rolls the whole thing back. By
 * the time the failure reaches here the database is intact and still at its old
 * version; all this has to do is recognise the refusal Dexie has wrapped and
 * hand it to the page rather than let it surface as `AbortError`.
 *
 * A version that *drops* a durable store cannot be caught that way — the rows
 * are gone before any callback runs, and there is nothing left to count. So that
 * one is refused before the database is opened at all, by comparing what is on
 * disk against what the new schema declares. It is a belt-and-braces check: the
 * real guarantee is `migrations.test.ts`, which fails the build if a durable
 * store ever stops being declared. A runtime check cannot be the guarantee,
 * because by the time it runs the code that would drop the store has shipped.
 */
import Dexie from "dexie"

import { countDurableWork, shortfallsBetween, tablesIn, type DurableCensus } from "./migrations/census"
import { MigrationRefused, refusalWithin } from "./migrations/refusal"
import { offlineDatabase, type OfflineDatabase } from "./schema"

export interface OpenOutcome {
  /** Null when the database was left unopened rather than upgraded. */
  db: OfflineDatabase | null
  refusal: MigrationRefused | null
}

export async function openOfflineDatabase(db: OfflineDatabase = offlineDatabase()): Promise<OpenOutcome> {
  if (db.isOpen()) return { db, refusal: null }

  const preflight = await droppedStoreRefusal(db)
  if (preflight !== null) return { db: null, refusal: preflight }

  try {
    await db.open()
  } catch (error) {
    const refusal = refusalWithin(error)
    if (refusal === null) throw error

    // Dexie leaves the connection in a failed state after an aborted upgrade,
    // and a later `open()` on the same instance replays the same failure. Closed
    // here so that a page which recovers — an operator who exported and then
    // asked for help — is not fighting a poisoned handle as well.
    db.close()

    return { db: null, refusal }
  }

  return { db, refusal: null }
}

/**
 * What is on disk, read without imposing a schema on it.
 *
 * Dexie opens a database with no declared versions in "dynamic" mode, adopting
 * whatever schema it finds. That is the only way to count rows in a store the
 * new code may no longer know about — which is precisely the store worth
 * counting.
 */
async function existingCensus(name: string): Promise<{ census: DurableCensus; version: number } | null> {
  if (!(await Dexie.exists(name))) return null

  const probe = new Dexie(name)

  try {
    await probe.open()

    return { census: await countDurableWork(probe), version: probe.verno }
  } catch {
    // A database that will not open dynamically will not open under the real
    // schema either, and the failure there carries more information than a
    // guess here would. Fall through and let the real open report it.
    return null
  } finally {
    probe.close()
  }
}

/**
 * Exported for its own test rather than only through `openOfflineDatabase`: the
 * case it guards cannot be provoked through the real schema, which declares
 * every durable store, so the only way to exercise it is directly.
 */
export async function droppedStoreRefusal(db: Dexie): Promise<MigrationRefused | null> {
  const existing = await existingCensus(db.name)
  if (existing === null) return null

  // `tables` is populated when `.version().stores()` runs in the constructor,
  // not when the database opens, so the declared set is readable here — before
  // anything has been migrated.
  const declared = tablesIn(db)
  const after: DurableCensus = {}
  for (const [ store, count ] of Object.entries(existing.census)) {
    if (declared.has(store)) after[store] = count
  }

  const shortfalls = shortfallsBetween(existing.census, after)
  if (shortfalls.length === 0) return null

  return new MigrationRefused(db.verno, shortfalls)
}
